import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DetailedPageLayout from '../components/DetailedPageLayout';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomBarChart from '../../../components/Charts/CustomBarChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';
import StatCard from '../components/ui/StatCard';
import SectionCard from '../components/ui/SectionCard';
import DataTable from '../components/ui/DataTable';
import dashboardAPI from '../../../services/dashboardService';
import { Share2, Heart, MessageCircle } from 'lucide-react';

const TOP_POST_COLUMNS = [
    {
        key: 'content',
        header: 'Content',
        render: (row) => (
            <p className="sd-post-content" title={row.content}>
                {row.content || 'No content'}
            </p>
        )
    },
    { key: 'author', header: 'Author' },
    {
        key: 'type',
        header: 'Type',
        width: '100px',
        render: (row) => (
            <span className="sd-post-type-badge">
                {row.type || 'Post'}
            </span>
        )
    },
    {
        key: 'engagementCount',
        header: 'Engagement',
        width: '110px',
        render: (row) => (
            <span className="sd-post-engagement">{(row.engagementCount || 0).toLocaleString()}</span>
        )
    }
];

const DetailedSocialPage = ({ isSubView = false, filters: externalFilters, onFilterChange: externalOnFilterChange }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localFilters, setLocalFilters] = useState({ timeRange: '30days', category: 'all' });

    const filters = externalFilters || localFilters;

    const fetchStats = useCallback(async (newFilters = filters) => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getSocialStats(newFilters);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch social stats', error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const onFilterChange = externalOnFilterChange || ((newFilters) => {
        setLocalFilters(newFilters);
        fetchStats(newFilters);
    });

    useEffect(() => {
        fetchStats(filters);
    }, [fetchStats, filters]);

    const postTypeData = useMemo(() => {
        if (!stats?.postsByType) return [];
        return Object.entries(stats.postsByType).map(([key, value]) => ({
            name: key.charAt(0).toUpperCase() + key.slice(1),
            value
        }));
    }, [stats?.postsByType]);

    const graphData = stats?.graph_data || [];

    if (loading || !stats) {
        return <div className="sd-loading-message">Loading statistics...</div>;
    }

    return (
        <DetailedPageLayout
            title="Social Analytics"
            onFilterChange={onFilterChange}
            filters={filters}
            isSubView={isSubView}
        >
            <div className="sd-grid sd-grid--kpi-users">
                <StatCard label="Total Posts" value={stats.totalPosts} icon={Share2} color="bg-violet-500" />
                <StatCard label="Total Likes" value={stats.likes} icon={Heart} color="bg-pink-500" />
                <StatCard label="Total Comments" value={stats.comments} icon={MessageCircle} color="bg-blue-500" />
            </div>

            <div className="sd-grid sd-grid--charts">
                <SectionCard title="Content Distribution">
                    <CustomDonutChart
                        data={postTypeData}
                        totalValue={stats.totalPosts}
                        title=""
                    />
                </SectionCard>
                <SectionCard title="Engagement Trends" className="sd-card--wide">
                    <CustomAreaChart data={graphData} color="#8b5cf6" title="" />
                </SectionCard>
            </div>

            <SectionCard title="Top Performing Posts">
                <DataTable
                    columns={TOP_POST_COLUMNS}
                    data={stats.topPosts || []}
                    emptyText="No top posts available"
                />
            </SectionCard>
        </DetailedPageLayout>
    );
};

export default DetailedSocialPage;
